import api from '~/utils/api';
import { billingResponseData, isStripeHostedUrl } from '~/utils/billing-route.mjs';

export interface CopyrightConfig {
  enabled: boolean;
  // Standalone purchase permission for the authenticated account, independent of membership.
  eligible: boolean;
  amount: number;
  currency: string;
  kind: string;
  order_count: number;
}

export interface CertificateState {
  task_id: string;
  music_id: string;
  scope?: 'task' | 'song';
  status: 'none' | 'pending' | 'processing' | 'issued' | 'legacy_overlap';
  order_id?: string;
  music_ids?: string[];
  legacy_order_ids?: string[];
}

export interface CertificateSongSnapshot { music_id: string; song_title: string; song_created_at: number }
export interface SongCertificate {
  id: string;
  task_id: string;
  music_id: string;
  scope?: 'task' | 'song';
  music_ids?: string[];
  songs?: CertificateSongSnapshot[];
  owner: string;
  song_title: string;
  song_created_at: number;
  status: 'pending' | 'processing' | 'issued' | 'refunded' | 'failed' | 'expired';
  kind: string;
  terms_version: string;
  cert_number?: string;
  issued_at?: number;
  created: number;
  amount_paid?: number;
  currency?: string;
  receipt_url?: string;
  refund_review?: boolean;
}

export interface CertificateSong {
  task_id: string;
  music_id: string;
  scope?: 'task' | 'song';
  music_ids?: string[];
  songs?: CertificateSongSnapshot[];
  overlap_orders?: SongCertificate[];
  song_title: string;
  created: number;
  order: SongCertificate | null;
}

export const copyrightService = {
  async states(songs: Array<{ task_id: string; music_id?: string; scope?: 'task' | 'song' }>): Promise<CertificateState[]> {
    return billingResponseData(await api.post('music/v1/copyright/states', { songs }));
  },
  async config(): Promise<CopyrightConfig> {
    return billingResponseData(await api.post('music/v1/copyright/config', {}));
  },
  async song(taskId: string, musicId = '', scope: 'task' | 'song' = musicId ? 'song' : 'task'): Promise<CertificateSong> {
    return billingResponseData(await api.post('music/v1/copyright/song', { task_id: taskId, ...(scope === 'task' ? { scope } : { music_id: musicId }) }));
  },
  async checkout(taskId: string, musicId: string, owner: string, scope: 'task' | 'song' = musicId ? 'song' : 'task') {
    const result = billingResponseData(await api.post('music/v1/copyright/checkout', { task_id: taskId, ...(scope === 'task' ? { scope } : { music_id: musicId }), owner }));
    if (!isStripeHostedUrl(result.url)) throw new Error('Invalid copyright checkout URL');
    return result as { url: string; order_id: string; session_id: string };
  },
  async order(orderId: string): Promise<SongCertificate> {
    return billingResponseData(await api.post('music/v1/copyright/order', { order_id: orderId }));
  },
  async page(page = 1, query = ''): Promise<{ list: SongCertificate[]; total: number; size: number }> {
    return billingResponseData(await api.post('music/v1/copyright/page', { page, size: 25, query }));
  },
};
